import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_URL } from "@/lib/siteUrl";
import { legacyCanonicalPath } from "@/lib/legacyRedirects";

export { SITE_URL };
export const SITE_NAME = "California Waste Solutions";
const SHORT_SITE_NAME = "CWS";
export const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/7zCHccIuTaTFfB9ixlxAxeKu5rE3/social-images/social-1777844700029-California_Waste_Solution_Facility_in_Oakland_.webp";

interface ExtraMeta {
  property?: string;
  name?: string;
  content: string;
}

interface SeoProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  imageAlt?: string;
  type?: "website" | "article";
  noindex?: boolean;
  /**
   * "noindex, follow" instead of "noindex,nofollow". Used on the 404 route so
   * dead URLs drop out of the index while their links still get crawled.
   */
  followWhenNoindex?: boolean;
  keywords?: string[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  publishedTime?: string;
  modifiedTime?: string;
  prevUrl?: string;
  nextUrl?: string;
  extraMeta?: ExtraMeta[];
  rssUrl?: string;
}

// Truncate at a word boundary, never exceeding `max` chars (no ellipsis added — clean for SEO).
const truncate = (s: string, max: number) => {
  if (!s) return s;
  const clean = s.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trim();
};

// Escape characters that could break out of a <script> tag or violate JSON in HTML.
// Prevents stored XSS via DB-sourced fields rendered into JSON-LD.
const safeJsonLd = (v: unknown) =>
  JSON.stringify(v)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");

const absolutize = (img: string) => {
  if (!img) return DEFAULT_OG_IMAGE;
  if (/^https?:\/\//i.test(img)) return img;
  if (img.startsWith("/")) return `${SITE_URL}${img}`;
  return img;
};

/**
 * One canonical form per page. Query and hash are dropped, a trailing slash is
 * stripped (the site root stays "/"), and a legacy redirect path resolves to
 * its destination so /commercial-recycling-oakland and /oakland/ never look
 * like duplicates of /commercial and /oakland.
 */
export const canonicalPath = (input: string): string => {
  const raw = (input || "/").split("#")[0].split("?")[0];
  const trimmed = raw.length > 1 ? raw.replace(/\/+$/, "") : raw;
  const normalized = trimmed || "/";
  return legacyCanonicalPath(normalized) ?? normalized;
};

export const Seo = ({
  title,
  description,
  path,
  image,
  imageAlt = "California Waste Solutions recycling and resource recovery services",
  type = "website",
  noindex = false,
  followWhenNoindex = false,
  keywords,
  jsonLd,
  publishedTime,
  modifiedTime,
  prevUrl,
  nextUrl,
  extraMeta,
  rssUrl = "https://hnxzmedhuxtcuwoidkcr.supabase.co/functions/v1/news-rss",
}: SeoProps) => {
  const location = useLocation();
  const pathname = canonicalPath(path ?? location.pathname);
  const canonical = `${SITE_URL}${pathname === "/" ? "" : pathname}`;
  const fullTitle = (() => {
    if (pathname === "/") return title;
    const hasFullSuffix = title.endsWith(` | ${SITE_NAME}`);
    const hasShortSuffix = title.endsWith(` | ${SHORT_SITE_NAME}`);
    const baseTitle = hasFullSuffix
      ? title.slice(0, -(` | ${SITE_NAME}`).length)
      : hasShortSuffix
        ? title.slice(0, -(` | ${SHORT_SITE_NAME}`).length)
        : title;
    if (!hasFullSuffix && !hasShortSuffix && title.toLowerCase().includes(SITE_NAME.toLowerCase())) return title;
    const longTitle = `${baseTitle} | ${SITE_NAME}`;
    if (longTitle.length <= 65) return longTitle;
    const shortTitle = `${baseTitle} | ${SHORT_SITE_NAME}`;
    return shortTitle.length <= 65 ? shortTitle : baseTitle;
  })();
  const fullDescription = truncate(description || "", 160);
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const finalImage = absolutize(image || DEFAULT_OG_IMAGE);
  const robots = noindex
    ? (followWhenNoindex ? "noindex,follow" : "noindex,nofollow")
    : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

  // Prerendered pages ship a full per-route head (marked data-prerender-head by
  // scripts/prerender-news.mjs). Helmet only replaces the tags it manages, so
  // without this those static copies stay alongside Helmet's and the page ends
  // up with two canonicals. Retire the marked copies ONLY once Helmet's own
  // canonical is in the document: if hydration or Helmet never runs, the
  // prerendered tags are left untouched and the page keeps correct metadata.
  useEffect(() => {
    let frame = 0;
    let attempts = 0;
    const sweep = () => {
      const helmetWrote = document.head.querySelector(
        'link[rel="canonical"][data-rh]',
      );
      if (helmetWrote) {
        document.head
          .querySelectorAll("[data-prerender-head]")
          .forEach((node) => node.remove());
        return;
      }
      if (++attempts < 60) frame = requestAnimationFrame(sweep);
    };
    sweep();
    return () => cancelAnimationFrame(frame);
  });



  // JSON-LD is managed imperatively, not through Helmet, so it can REPLACE the
  // prerendered blocks (which carry the same data-seo-jsonld marker) instead of
  // appending duplicates. Googlebot renders JS, so without this a prerendered
  // JobPosting plus a hydrated one would report as two identical items.
  const ldSerialized = ldArray.map((ld) => safeJsonLd(ld)).join("\u0000");
  useEffect(() => {
    const payloads = ldSerialized ? ldSerialized.split("\u0000") : [];

    // Collect the @id values we are about to inject so we can remove any
    // prerendered sitewide block that would otherwise duplicate the same node.
    const injectedIds = new Set<string>();
    for (const json of payloads) {
      try {
        const parsed = JSON.parse(json) as Record<string, unknown>;
        if (typeof parsed["@id"] === "string") injectedIds.add(parsed["@id"]);
      } catch {
        // leave block alone on parse failure
      }
    }

    document
      .querySelectorAll('script[type="application/ld+json"][data-sitewide-jsonld]')
      .forEach((n) => {
        try {
          const parsed = JSON.parse(n.textContent || "") as Record<string, unknown>;
          if (typeof parsed["@id"] === "string" && injectedIds.has(parsed["@id"])) {
            n.remove();
          }
        } catch {
          // leave block alone on parse failure
        }
      });

    document
      .querySelectorAll('script[type="application/ld+json"][data-seo-jsonld]')
      .forEach((n) => n.remove());

    const added = payloads.map((json) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo-jsonld", "1");
      el.textContent = json;
      document.head.appendChild(el);
      return el;
    });
    return () => added.forEach((el) => el.remove());
  }, [ldSerialized]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en-US" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <link rel="alternate" type="application/rss+xml" title={`${SITE_NAME} Newsroom`} href={rssUrl} />
      {prevUrl && <link rel="prev" href={prevUrl} />}
      {nextUrl && <link rel="next" href={nextUrl} />}
      <meta name="robots" content={robots} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="fb:app_id" content="0" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {extraMeta?.map((m, i) =>
        m.property
          ? <meta key={`p-${i}`} property={m.property} content={m.content} />
          : <meta key={`n-${i}`} name={m.name} content={m.content} />
      )}

    </Helmet>
  );
};

export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/**
 * David Duong has a dedicated profile page at /david-duong, so his Person node
 * is anchored there. Every page that emits his node, and the Organization
 * founder reference, use this one id so the graph has a single entity.
 */
export const DAVID_DUONG_PERSON_ID = `${SITE_URL}/david-duong#person`;

/**
 * Leaders with their own profile page under /leadership/<slug>. Their Person
 * node is anchored on that page, exactly like David's is anchored on
 * /david-duong. Keep in sync with LEADER_PROFILES in src/data/leaderProfiles.ts
 * and with LEADER_PERSON_SLUGS in scripts/prerender-news.mjs.
 */
export const LEADER_PROFILE_PERSON_SLUGS = ["michael-duong", "johnny-duong", "kristina-duong", "victor-duong"];

const slugifyName = (name: string) =>
  name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** Canonical page a person's Person node is anchored to, if they have one. */
export const personPagePath = (name: string) => {
  const slug = slugifyName(name);
  if (slug === "david-duong") return "/david-duong";
  if (LEADER_PROFILE_PERSON_SLUGS.includes(slug)) return `/leadership/${slug}`;
  return null;
};

/** Stable Person node id so the same person is one entity across every page. */
export const personId = (name: string) => {
  const slug = slugifyName(name);
  if (slug === "david-duong") return DAVID_DUONG_PERSON_ID;
  if (LEADER_PROFILE_PERSON_SLUGS.includes(slug)) return `${SITE_URL}/leadership/${slug}#person`;
  return `${SITE_URL}/#person-${slug}`;
};

/** Every leader whose Person node has its own page, for Organization.employee. */
export const LEADER_EMPLOYEE_REFS = [
  { "@id": DAVID_DUONG_PERSON_ID },
  ...LEADER_PROFILE_PERSON_SLUGS.map((slug) => ({ "@id": `${SITE_URL}/leadership/${slug}#person` })),
];

export const orgJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Corporation",
  "@id": ORG_ID,
  name: SITE_NAME,
  alternateName: ["CWS", "Cal Waste", "California Waste Solutions Inc."],
  legalName: "California Waste Solutions, Inc.",
  url: SITE_URL,
  logo: `${SITE_URL}/images/cws-logo.png`,
  image: DEFAULT_OG_IMAGE,
  foundingDate: "1992",
  founder: { "@id": personId("David Duong") },
  employee: LEADER_EMPLOYEE_REFS,
  slogan: "Recycling and Resource Recovery for a Circular Future",
  description:
    "California Waste Solutions is a privately held, family owned recycling and resource recovery company founded in Oakland, California in 1992. The company provides municipal curbside recycling collection, multifamily and commercial recycling service, materials recovery facility operations, container and cart support, and recycling education for Oakland and San Jose, with international operations through Vietnam Waste Solutions.",
  areaServed: [
    { "@type": "AdministrativeArea", name: "California" },
    { "@type": "City", name: "Oakland" },
    { "@type": "City", name: "San Jose" },
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "Vietnam" },
  ],
  knowsAbout: [
    "Municipal recycling",
    "Resource recovery",
    "Materials recovery facility",
    "MRF operations",
    "Commercial recycling",
    "Multifamily recycling",
    "Curbside collection",
    "Used motor oil recycling",
    "Bulky item pickup",
    "Environmental infrastructure",
    "Sustainable waste management",
    "Circular economy",
    "Zero waste programs",
    "Diversion and contamination reduction",
  ],
  subOrganization: {
    "@type": "Organization",
    "@id": `${SITE_URL}/#vietnam-waste-solutions`,
    name: "Vietnam Waste Solutions",
    parentOrganization: { "@id": ORG_ID },
    description:
      "Global environmental infrastructure and integrated waste management operations in Vietnam.",
    areaServed: { "@type": "Country", name: "Vietnam" },
  },
  sameAs: [
    "https://www.facebook.com/calwastesolutions/",
    "https://www.linkedin.com/company/californiawastesolutions",
    "https://www.instagram.com/californiawastesolutions/",
    "https://www.youtube.com/@CaliforniaWasteSolutions",
    "https://www.crunchbase.com/organization/california-waste-solutions",
    "https://www.bloomberg.com/profile/company/0194658D:US",
    "https://www.yelp.com/biz/california-waste-solutions-oakland-4",
    "https://www.wikidata.org/wiki/Q141497453",
  ],
  address: [
    {
      "@type": "PostalAddress",
      streetAddress: "1820 10th Street",
      addressLocality: "Oakland",
      addressRegion: "CA",
      postalCode: "94607",
      addressCountry: "US",
    },
    {
      "@type": "PostalAddress",
      streetAddress: "1120 Berryessa Road",
      addressLocality: "San Jose",
      addressRegion: "CA",
      postalCode: "95133",
      addressCountry: "US",
    },
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+1-510-625-5263",
      contactType: "customer service",
      areaServed: "Oakland, CA",
      availableLanguage: ["English", "Spanish", "Vietnamese", "Chinese"],
    },
    {
      "@type": "ContactPoint",
      telephone: "+1-408-213-7800",
      contactType: "customer service",
      areaServed: "San Jose, CA",
      availableLanguage: ["English", "Spanish", "Vietnamese"],
    },
  ],
});

export const websiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: "en-US",
  publisher: { "@id": ORG_ID },
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/news?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const serviceJsonLd = (s: {
  name: string;
  description: string;
  path: string;
  serviceType?: string;
  areaServed?: string | string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: s.name,
  description: s.description,
  serviceType: s.serviceType ?? s.name,
  provider: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/images/cws-logo.png`,
  },
  areaServed: Array.isArray(s.areaServed)
    ? s.areaServed.map((a) => ({ "@type": "AdministrativeArea", name: a }))
    : s.areaServed
      ? { "@type": "AdministrativeArea", name: s.areaServed }
      : [
          { "@type": "AdministrativeArea", name: "California" },
          { "@type": "City", name: "Oakland" },
          { "@type": "City", name: "San Jose" },
        ],
  url: `${SITE_URL}${s.path}`,
});

/**
 * One Service node per service per city, for the "Services in Oakland" and
 * "Services in San José" lists. provider is an @id reference to the
 * Organization node so the services attach to the existing company entity
 * rather than creating a second one.
 */
export const cityServiceJsonLd = (
  city: "Oakland" | "San Jose",
  s: { name: string; serviceType: string; description: string; path?: string },
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: `${s.name} in ${city === "San Jose" ? "San Jose" : "Oakland"}`,
  serviceType: s.serviceType,
  description: s.description,
  provider: { "@id": ORG_ID },
  areaServed: { "@type": "City", name: city },
  ...(s.path ? { url: `${SITE_URL}${s.path}` } : {}),
});



export const itemListJsonLd = (
  items: { name: string; path: string; description?: string }[],
  listName = "California Waste Solutions Services",
) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: listName,
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    url: `${SITE_URL}${it.path}`,
    ...(it.description ? { description: it.description } : {}),
  })),
});

export const localBusinessJsonLd = (city: "Oakland" | "San Jose") => {
  const data =
    city === "Oakland"
      ? {
          name: "California Waste Solutions, Oakland",
          telephone: "+1-510-625-5263",
          email: "OaklandCS@calwaste.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "1820 10th Street",
            addressLocality: "Oakland",
            addressRegion: "CA",
            postalCode: "94607",
            addressCountry: "US",
          },
        }
      : {
          name: "California Waste Solutions, San José",
          telephone: "+1-408-213-7800",
          email: "customerservice@calwaste.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "1120 Berryessa Road",
            addressLocality: "San Jose",
            addressRegion: "CA",
            postalCode: "95133",
            addressCountry: "US",
          },
        };
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    url: SITE_URL,
    image: `${SITE_URL}/images/cws-logo.png`,
    priceRange: "$$",
    parentOrganization: { "@id": ORG_ID },
    ...data,
  };
};

export const breadcrumbJsonLd = (items: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path === "/" ? "" : it.path}`,
  })),
});

export const faqJsonLd = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

/**
 * VideoObject for a video that plays on a page. uploadDate must be the real
 * date the video file entered this repository (from git log), never invented.
 */
export const videoObjectJsonLd = (v: {
  name: string;
  description: string;
  thumbnailUrl: string;
  contentUrl: string;
  uploadDate: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: v.name,
  description: v.description,
  thumbnailUrl: absolutize(v.thumbnailUrl),
  contentUrl: absolutize(v.contentUrl),
  uploadDate: v.uploadDate,
  publisher: { "@id": ORG_ID },
});

export const personJsonLd = (p: {
  name: string;
  jobTitle: string;
  path?: string;
  image?: string;
  sameAs?: string[];
  subjectOf?: Record<string, unknown>;
  memberOf?: Record<string, unknown>;
  parent?: { "@id": string };
  sibling?: { "@id": string }[];
}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": personId(p.name),
  name: p.name,
  jobTitle: p.jobTitle,
  worksFor: { "@id": ORG_ID },
  url: `${SITE_URL}${personPagePath(p.name) ?? p.path ?? "/about"}`,
  ...(p.image ? { image: absolutize(p.image) } : {}),
  ...(p.sameAs && p.sameAs.length ? { sameAs: p.sameAs } : {}),
  ...(p.subjectOf ? { subjectOf: p.subjectOf } : {}),
  ...(p.memberOf ? { memberOf: p.memberOf } : {}),
  ...(p.parent ? { parent: p.parent } : {}),
  ...(p.sibling ? { sibling: p.sibling } : {}),
});

export const contactPageJsonLd = (path = "/contact") => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: `Contact ${SITE_NAME}`,
  url: `${SITE_URL}${path}`,
  isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
});

export const webPageJsonLd = (p: { name: string; description: string; path: string }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: p.name,
  description: p.description,
  url: `${SITE_URL}${p.path}`,
  isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
});

export const collectionPageJsonLd = (p: { name: string; description: string; path: string }) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: p.name,
  description: p.description,
  url: `${SITE_URL}${p.path}`,
  isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
});

export const articleJsonLd = (a: {
  title: string;
  description: string;
  path: string;
  image?: string;
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: a.title,
  description: a.description,
  image: a.image ? [a.image] : undefined,
  datePublished: a.publishedTime,
  dateModified: a.modifiedTime ?? a.publishedTime,
  author: { "@type": "Organization", name: a.authorName ?? SITE_NAME },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/images/cws-logo.png` },
  },
  mainEntityOfPage: `${SITE_URL}${a.path}`,
});

const EMPLOYMENT_TYPE_MAP: Record<string, string> = {
  fulltime: "FULL_TIME",
  parttime: "PART_TIME",
  contract: "CONTRACTOR",
  temp: "TEMPORARY",
  temporary: "TEMPORARY",
  seasonal: "TEMPORARY",
  intern: "INTERN",
  internship: "INTERN",
};

const mapEmploymentType = (v?: string | null) => {
  const key = (v || "").toLowerCase().replace(/[\s_-]+/g, "");
  return EMPLOYMENT_TYPE_MAP[key] || "FULL_TIME";
};

// Verified CWS work locations. Oakland is the HQ address; San José roles are
// reported at city level because the site address varies by assignment.
const OAKLAND_PLACE = {
  "@type": "Place",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1211 Embarcadero, Suite 300",
    addressLocality: "Oakland",
    addressRegion: "CA",
    postalCode: "94606",
    addressCountry: "US",
  },
};

const SAN_JOSE_PLACE = {
  "@type": "Place",
  address: {
    "@type": "PostalAddress",
    addressLocality: "San José",
    addressRegion: "CA",
    addressCountry: "US",
  },
};

const jobPlaces = (location?: string | null) => {
  const l = (location || "").toLowerCase();
  const isSanJose = l.includes("san jose") || l.includes("san josé");
  const isOakland = l.includes("oakland") || !isSanJose;
  const places = [];
  if (isOakland) places.push(OAKLAND_PLACE);
  if (isSanJose) places.push(SAN_JOSE_PLACE);
  return places.length === 1 ? places[0] : places;
};

const jobDescriptionHtml = (job: {
  title: string;
  summary?: string | null;
  description?: string | null;
  responsibilities?: string | null;
  requirements?: string | null;
  preferred_qualifications?: string | null;
  benefits?: string[] | null;
}) => {
  const block = (heading: string, body?: string | null) => {
    const text = (body || "").trim();
    if (!text) return "";
    return `<h3>${heading}</h3><p>${text.replace(/\n+/g, "<br />")}</p>`;
  };
  const parts = [
    block("About the role", job.summary),
    block("Description", job.description),
    block("Responsibilities", job.responsibilities),
    block("Requirements", job.requirements),
    block("Preferred qualifications", job.preferred_qualifications),
    Array.isArray(job.benefits) && job.benefits.length > 0
      ? `<h3>Benefits</h3><ul>${job.benefits.map((b) => `<li>${b}</li>`).join("")}</ul>`
      : "",
  ].filter(Boolean);
  return parts.join("") || `<p>${job.title} at ${SITE_NAME}.</p>`;
};

/**
 * Parse a free-text pay range such as "$68,000 - $80,000 a year",
 * "$24.50/hr", "$22 - $28 per hour" into a schema.org MonetaryAmount.
 * Returns undefined when no numbers can be read.
 */
const jobBaseSalary = (payRange?: string | null) => {
  const raw = (payRange || "").trim();
  if (!raw) return undefined;
  const nums = (raw.match(/\d[\d,]*(?:\.\d+)?/g) || [])
    .map((n) => Number(n.replace(/,/g, "")))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (nums.length === 0) return undefined;

  const lower = raw.toLowerCase();
  let unitText = "YEAR";
  if (/hour|hourly|\/\s*hr|per hr|hr\b/.test(lower)) unitText = "HOUR";
  else if (/week/.test(lower)) unitText = "WEEK";
  else if (/month/.test(lower)) unitText = "MONTH";
  else if (!/year|annual|\/\s*yr/.test(lower) && Math.max(...nums) < 200) unitText = "HOUR";

  const minValue = Math.min(...nums);
  const maxValue = Math.max(...nums);
  return {
    "@type": "MonetaryAmount",
    currency: "USD",
    value: {
      "@type": "QuantitativeValue",
      minValue,
      maxValue,
      unitText,
    },
  };
};

export const jobPostingJsonLd = (job: {
  id?: string;
  title: string;
  description?: string | null;
  summary?: string | null;
  responsibilities?: string | null;
  requirements?: string | null;
  preferred_qualifications?: string | null;
  benefits?: string[] | null;
  department?: string | null;
  employment_type?: string | null;
  posted_at?: string | null;
  closes_at?: string | null;
  location?: string | null;
  pay_range?: string | null;
  slug: string;
}) => {
  const applyUrl = `${SITE_URL}/careers/${job.slug}`;
  // Only emit validThrough when HR set a real close date. A synthesized date
  // would tell Google the role stays open longer than we can stand behind.
  const validThrough = job.closes_at || undefined;

  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: jobDescriptionHtml(job),
    identifier: {
      "@type": "PropertyValue",
      name: SITE_NAME,
      value: job.id || job.slug,
    },
    datePosted: job.posted_at || undefined,
    validThrough,
    employmentType: mapEmploymentType(job.employment_type),
    industry: "Recycling and Resource Recovery",
    occupationalCategory: job.department || undefined,
    hiringOrganization: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      sameAs: SITE_URL,
      logo: `${SITE_URL}/images/cws-logo.png`,
    },
    jobLocation: jobPlaces(job.location),
    baseSalary: jobBaseSalary(job.pay_range),
    directApply: true,
    url: applyUrl,
    mainEntityOfPage: applyUrl,
  };
};


/**
 * People with their own Person node that news posts can link to. Used to build
 * NewsArticle "about" (named in the title) and "mentions" (found in the body)
 * so each article resolves to the same entities as the profile pages.
 */
const LINKABLE_ARTICLE_PEOPLE = [
  "David Duong",
  "Michael Duong",
  "Johnny Duong",
  "Kristina Duong",
  "Victor Duong",
];

const KING_OF_TRASH_MOVIE = {
  "@type": "Movie",
  name: "The King of Trash",
  url: "https://www.thekingoftrash.us",
  sameAs: ["https://www.imdb.com/title/tt39126990/"],
};

export const newsArticleEntities = (title?: string, body?: string) => {
  const titleText = String(title || "");
  const bodyText = String(body || "");
  const has = (text: string, needle: string) =>
    text.toLowerCase().includes(needle.toLowerCase());

  const about: Array<Record<string, unknown>> = [];
  const mentions: Array<Record<string, unknown>> = [];

  for (const name of LINKABLE_ARTICLE_PEOPLE) {
    const ref = { "@type": "Person", "@id": personId(name), name };
    if (has(titleText, name)) about.push(ref);
    else if (has(bodyText, name)) mentions.push(ref);
  }

  if (has(titleText, "King of Trash") || has(bodyText, "King of Trash")) {
    mentions.push(KING_OF_TRASH_MOVIE);
  }

  return {
    ...(about.length ? { about } : {}),
    ...(mentions.length ? { mentions } : {}),
  };
};

export const newsArticleJsonLd = (a: {
  title: string;
  description: string;
  path: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
  authorIsPerson?: boolean;
  articleSection?: string;
  keywords?: string[];
  wordCount?: number;
  articleBody?: string;
  /** Post title used for entity linking (defaults to the SEO title). */
  entityTitle?: string;
  /** Raw post body used to detect mentioned people and the film. */
  entityBody?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "NewsArticle",
  headline: a.title,
  description: a.description,
  image: a.image
    ? [{
        "@type": "ImageObject",
        url: a.image,
        width: a.imageWidth ?? 1200,
        height: a.imageHeight ?? 630,
      }]
    : undefined,
  datePublished: a.publishedTime,
  dateModified: a.modifiedTime ?? a.publishedTime,
  author: a.authorName
    ? {
        "@type": a.authorIsPerson ? "Person" : "Organization",
        name: a.authorName,
        ...(a.authorIsPerson ? { url: `${SITE_URL}/about` } : { url: SITE_URL }),
      }
    : { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  publisher: {
    "@type": "Organization",
    "@id": ORG_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/images/cws-logo.png`, width: 600, height: 60 },
  },
  ...newsArticleEntities(a.entityTitle ?? a.title, a.entityBody),
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${a.path}` },
  isAccessibleForFree: true,
  inLanguage: "en-US",
  ...(a.articleSection ? { articleSection: a.articleSection } : {}),
  ...(a.keywords && a.keywords.length ? { keywords: a.keywords.join(", ") } : {}),
  ...(a.wordCount ? { wordCount: a.wordCount } : {}),
  ...(a.articleBody ? { articleBody: a.articleBody } : {}),
});

export const speakableJsonLd = (cssSelectors: string[] = ["h1", ".lead"]) => ({
  "@context": "https://schema.org",
  "@type": "SpeakableSpecification",
  cssSelector: cssSelectors,
});
